<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useAdmin } from 'dashboard/composables/useAdmin';
import ContactInfoRow from './ContactInfoRow.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SocialIcons from './SocialIcons.vue';
import EditContact from './EditContact.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import NextButton from 'dashboard/components-next/button/Button.vue';
//WAVOIP - Softphone
import SoftphoneMinimized from 'dashboard/components/widgets/softphone/SoftphoneMinimized.vue';
import WavoipIcon from 'dashboard/assets/softphone/wavoip-icon.svg';
//Import Flags Condition
import { FEATURE_FLAGS } from '../../../../featureFlags';

import {
  isAConversationRoute,
  isAInboxViewRoute,
  getConversationDashboardRoute,
} from '../../../../helper/routeHelpers';
import { emitter } from 'shared/helpers/mitt';

export default {
  components: {
    NextButton,
    ContactInfoRow,
    EditContact,
    Avatar,
    ComposeConversation,
    SocialIcons,
    ContactMergeModal,
    SoftphoneMinimized,
    WavoipIcon,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['panelClose'],
  setup() {
    const { isAdmin } = useAdmin();
    return {
      isAdmin,
    };
  },
  data() {
    return {
      showEditModal: false,
      showMergeModal: false,
      showDeleteModal: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      //WAVOIP - Softphone
      getInboxById: 'inboxes/getInbox',
      allInboxes: 'inboxes/getInboxes',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),

    // ✅ BUSCAR ID DO INBOX ATUAL (múltiplas fontes) - COM DEBUG
    currentInboxId() {
      console.log('🔍 DEBUG currentInboxId - Iniciando busca completa...', {
        // Dados da rota
        routeConversationId: this.$route.params.conversationId,
        routeInboxId: this.$route.params.inboxId,
        routeName: this.$route.name,
        
        // Dados do store (alternativa)
        hasCurrentChat: !!this.$store.getters.getSelectedChat,
        currentChatId: this.$store.getters.getSelectedChat?.id,
        currentChatInboxId: this.$store.getters.getSelectedChat?.inbox_id,
      });

      // PRIORIDADE 1: Inbox do chat ativo no store (mais confiável)
      try {
        const currentChat = this.$store.getters.getSelectedChat;
        if (currentChat?.inbox_id) {
          const inboxId = Number(currentChat.inbox_id);
          console.log('✅ DEBUG: Usando inbox do currentChat:', inboxId);
          return inboxId;
        }
      } catch (error) {
        console.error('❌ DEBUG: Erro ao buscar currentChat:', error);
      }

      // PRIORIDADE 2: ID da conversa atual da rota
      if (this.$route.params.conversationId) {
        try {
          const conversation = this.$store.getters['conversations/getConversation'](
            this.$route.params.conversationId
          );
          console.log('🔍 DEBUG conversation da rota:', {
            conversation: conversation,
            hasConversation: !!conversation,
            inboxId: conversation?.inbox_id
          });
          
          if (conversation?.inbox_id) {
            const inboxId = Number(conversation.inbox_id);
            console.log('✅ DEBUG: Usando inbox da conversa da rota:', inboxId);
            return inboxId;
          }
        } catch (error) {
          console.error('❌ DEBUG: Erro ao buscar conversa da rota:', error);
        }
      }
      
      // PRIORIDADE 3: ID da rota (para páginas de configuração)
      if (this.$route.params.inboxId) {
        const inboxId = Number(this.$route.params.inboxId);
        console.log('✅ DEBUG: Usando inbox da rota:', inboxId);
        return inboxId;
      }
      
      // PRIORIDADE 4: Verificar se tem alguma conversa ativa
      try {
        const allConversations = this.$store.getters.getAllConversations;
        console.log('🔍 DEBUG: Verificando conversas ativas:', {
          totalConversations: allConversations?.length || 0,
          firstConversation: allConversations?.[0]
        });
      } catch (error) {
        console.error('❌ DEBUG: Erro ao buscar conversas:', error);
      }
      
      console.log('❌ DEBUG: Nenhuma inbox encontrada em nenhuma fonte');
      return null;
    },

    // ✅ BUSCAR INBOX ATUAL COM BASE NO ID - COM DEBUG
    currentInbox() {
      const inboxId = this.currentInboxId;
      console.log('🔍 DEBUG currentInbox:', {
        inboxId,
        hasInboxId: !!inboxId
      });
      
      if (!inboxId) {
        console.log('❌ DEBUG: currentInboxId é null, retornando null');
        return null;
      }
      
      const inbox = this.getInboxById(inboxId);
      console.log('🔍 DEBUG inbox encontrada:', {
        inbox: inbox,
        hasInbox: !!inbox,
        inboxName: inbox?.name,
        channelType: inbox?.channel_type,
        wavoipEnabled: inbox?.wavoip_enabled,
        hasWavoipToken: !!inbox?.wavoip_token,
        hasExternalToken: !!inbox?.external_token
      });
      
      return inbox;
    },

    // FEATURE FLAGS - COM DEBUG
    isAPIInbox() {
      const result = this.currentInbox?.channel_type === 'Channel::Api';
      console.log('🔍 DEBUG isAPIInbox:', {
        channelType: this.currentInbox?.channel_type,
        isAPI: result
      });
      return result;
    },
    // Wavoip - Softphone
    isWavoipFeatureEnabled() {
      const result = this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.WAVOIP);
      console.log('DEBUG isWavoipFeatureEnabled:', {
        accountId: this.accountId,
        featureFlag: FEATURE_FLAGS.WAVOIP,
        isEnabled: result
      });
      return result;
    },
    // FreePBX
    isFreePBXFeatureEnabled() {
      const result = this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.FREEPBX);
      console.log('DEBUG isFreePBXFeatureEnabled:', {
        accountId: this.accountId,
        featureFlag: FEATURE_FLAGS.FREEPBX,
        isEnabled: result
      });
      return result;
    },

    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.additionalAttributes;
      return {
        twitter: twitterScreenName,
        telegram: telegramUsername,
        ...(socialProfiles || {}),
      };
    },
    // Delete Modal
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },

    //WAVOIP - Softphone
    //TOKEN WAVOIP (prioridade: wavoip_token > external_token) - COM DEBUG
    wavoipToken() {
      console.log('🔍 DEBUG wavoipToken:', {
        hasCurrentInbox: !!this.currentInbox,
        wavoipToken: this.currentInbox?.wavoip_token,
        externalToken: this.currentInbox?.external_token
      });
      
      if (this.currentInbox) {
        // PRIORIDADE 1: wavoip_token (campo específico)
        if (this.currentInbox.wavoip_token) {
          console.log('DEBUG: Usando wavoip_token:', this.currentInbox.wavoip_token.substring(0, 8) + '...');
          return this.currentInbox.wavoip_token;
        }
        
        // PRIORIDADE 2: external_token (fallback)
        if (this.currentInbox.external_token) {
          console.log('DEBUG: Usando external_token:', this.currentInbox.external_token.substring(0, 8) + '...');
          return this.currentInbox.external_token;
        }
      }
      
      console.log('DEBUG: Nenhum token encontrado');
      return null;
    },

    // WAVOIP HABILITADO (verificar campo boolean + existência de token) - COM DEBUG
    wavoipEnabled() {
      console.log('DEBUG wavoipEnabled:', {
        hasCurrentInbox: !!this.currentInbox,
        wavoipEnabledField: this.currentInbox?.wavoip_enabled,
        hasToken: !!(this.currentInbox?.wavoip_token || this.currentInbox?.external_token)
      });
      
      if (this.currentInbox) {
        // Verificar se wavoip_enabled está true E se tem pelo menos um token
        const isEnabled = Boolean(this.currentInbox.wavoip_enabled);
        const hasToken = Boolean(this.currentInbox.wavoip_token || this.currentInbox.external_token);
        
        const result = isEnabled && hasToken;
        console.log('DEBUG wavoipEnabled result:', {
          isEnabled,
          hasToken,
          finalResult: result
        });
        
        return result;
      }
      
      console.log('DEBUG: wavoipEnabled false (sem inbox)');
      return false;
    },

    // VERIFICAR SE PODE MOSTRAR BOTÃO - COM DEBUG
    showWavoipButton() {
      const enabled = this.wavoipEnabled;
      const hasToken = !!this.wavoipToken;
      const hasContact = !!this.contact?.id;
      const hasPhone = !!this.contactPhoneForWavoip;
      
      const result = Boolean(enabled && hasToken && hasContact && hasPhone);
      
      console.log('🔍 DEBUG showWavoipButton:', {
        enabled,
        hasToken,
        hasContact,
        hasPhone,
        contactName: this.contact?.name,
        contactPhone: this.contactPhoneForWavoip,
        finalResult: result
      });
      
      return result;
    },

    // NÚMERO FORMATADO PARA EXIBIÇÃO
    contactPhoneNumber() {
      if (!this.contact) return null;
      
      let phone = null;
      
      if (this.contact.phone_number) {
        phone = this.contact.phone_number;
      } else if (this.contact.additional_attributes?.phone) {
        phone = this.contact.additional_attributes.phone;
      }
      
      if (!phone) return null;
      
      return this.formatPhoneNumber(phone);
    },

    // NÚMERO LIMPO PARA WAVOIP API
    contactPhoneForWavoip() {
      if (!this.contact) return null;
      
      let phone = null;
      
      if (this.contact.phone_number) {
        phone = this.contact.phone_number;
      } else if (this.contact.additional_attributes?.phone) {
        phone = this.contact.additional_attributes.phone;
      }
      
      if (!phone) return null;
      
      return this.formatPhoneForWavoip(phone);
    },

  },
  watch: {
    'contact.id': {
      handler(id) {
        this.$store.dispatch('contacts/fetchContactableInbox', id);
      },
      immediate: true,
    },
  },
  methods: {
    dynamicTime,
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    openComposeConversationModal(toggleFn) {
      toggleFn();
      // Flag to prevent triggering drag n drop,
      // When compose modal is active
      emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
    },
    closeComposeConversationModal() {
      // Flag to enable drag n drop,
      // When compose modal is closed
      emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    confirmDeletion() {
      this.deleteContact(this.contact);
      this.closeDelete();
    },
    closeDelete() {
      this.showDeleteModal = false;
      this.showEditModal = false;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        if (!countryCode) {
          return `${cityAndCountry} 🌎`;
        }

        const code = countryCode?.toLowerCase();
        return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
      } catch (error) {
        return '';
      }
    },
    async deleteContact({ id }) {
      try {
        await this.$store.dispatch('contacts/delete', id);
        this.$emit('panelClose');
        useAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));

        if (isAConversationRoute(this.$route.name)) {
          this.$router.push({
            name: getConversationDashboardRoute(this.$route.name),
          });
        } else if (isAInboxViewRoute(this.$route.name)) {
          this.$router.push({
            name: 'inbox_view',
          });
        } else if (this.$route.name !== 'contacts_dashboard') {
          this.$router.push({
            name: 'contacts_dashboard',
          });
        }
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    closeMergeModal() {
      this.showMergeModal = false;
    },
    openMergeModal() {
      this.showMergeModal = true;
    },

    //WAVOIP - Softphone
    // ✅ WAVOIP: Método simplificado
    openSoftphone() {
      // console.log('🚀 Webphone: Iniciando chamada', {
      //   contactName: this.contact.name,
      //   phone: this.contactPhoneForWavoip,
      //   inboxId: this.currentInboxId,
      //   inboxName: this.currentInbox?.name,
      //   wavoip_token: this.currentInbox?.wavoip_token ? 'PRESENTE' : 'AUSENTE',
      //   external_token: this.currentInbox?.external_token ? 'PRESENTE' : 'AUSENTE',
      //   selectedToken: this.wavoipToken ? this.wavoipToken.substring(0, 8) + '...' : 'NENHUM',
      //   wavoip_enabled: this.currentInbox?.wavoip_enabled
      // });
      
      // Verificar se tem número de telefone
      if (!this.contactPhoneForWavoip) {
        useAlert('Número de telefone não encontrado para este contato');
        return;
      }

      // Verificar se tem token (prioritário: wavoip_token, fallback: external_token)
      if (!this.wavoipToken) {
        useAlert('Token Wavoip não encontrado. Verifique as configurações do inbox.');
        return;
      }

      // console.log('✅ Wavoip: Usando token:', {
      //   source: this.currentInbox?.wavoip_token ? 'wavoip_token' : 'external_token',
      //   token: this.wavoipToken.substring(0, 8) + '...'
      // });

      // Fazer a chamada diretamente
      this.$store.dispatch('softphone/makeCall', {
        token: this.wavoipToken,
        phone: this.contactPhoneForWavoip,
        contact_name: this.contact.name,
        chat_id: this.contact.id,
      }).then(() => {
        // console.log(' Webphone: Chamada iniciada com sucesso');
      }).catch((error) => {
        // console.error(' Webphone: Erro ao fazer chamada:', error);
        useAlert(`Erro ao fazer chamada: ${error.message}`);
      });
    },

    // ✅ FORMATAÇÃO: Para exibição (super simples - apenas + espaço + números)
    formatPhoneNumber(phone) {
      if (!phone) return null;
      
      // Remove tudo exceto números
      const cleanPhone = phone.replace(/[^\d]/g, '');
      
      if (!cleanPhone) return null;
      
      // Retorna: + espaço + números limpos
      return `+ ${cleanPhone}`;
    },

    //  FORMATAÇÃO: Para API Wavoip (com mapeamento de códigos de país)
    formatPhoneForWavoip(phone) {
      if (!phone) return null;
      
      // Remove tudo exceto números
      let cleanPhone = phone.replace(/[^\d]/g, '');
      
      // Mapeamento de códigos de país comuns e seus tamanhos esperados
      const countryPatterns = [
        { code: '1', minLength: 11, maxLength: 11 },    // EUA/Canadá
        { code: '55', minLength: 12, maxLength: 13 },   // Brasil
        { code: '44', minLength: 11, maxLength: 12 },   // Reino Unido
        { code: '49', minLength: 11, maxLength: 12 },   // Alemanha
        { code: '33', minLength: 10, maxLength: 11 },   // França
        { code: '86', minLength: 11, maxLength: 13 },   // China
        // Adicione mais países conforme necessário
      ];
      
      // Verificar se já tem um código de país válido
      for (const pattern of countryPatterns) {
        if (cleanPhone.startsWith(pattern.code) && 
            cleanPhone.length >= pattern.minLength && 
            cleanPhone.length <= pattern.maxLength) {
          return cleanPhone; // Já está no formato correto
        }
      }
      
      // Se não tem código de país, assumir Brasil para números de 10-11 dígitos
      if (cleanPhone.length >= 10 && cleanPhone.length <= 11) {
        return '55' + cleanPhone;
      }
      
      // Para outros casos, retornar como está (pode ser um código de país não mapeado)
      return cleanPhone;
    },
  },
}
</script>

<template>
  <div class="relative items-center w-full p-4">
    <div class="flex flex-col w-full gap-2 text-left rtl:text-right">
      <div class="flex flex-row justify-between">
        <Avatar
          v-if="showAvatar"
          :src="contact.thumbnail"
          :name="contact.name"
          :status="contact.availability_status"
          :size="48"
          hide-offline-status
          rounded-full
        />
      </div>

      <div class="flex flex-col items-start gap-1.5 min-w-0 w-full">
        <div v-if="showAvatar" class="flex items-center w-full min-w-0 gap-3">
          <h3
            class="flex-shrink max-w-full min-w-0 my-0 text-base capitalize break-words text-n-slate-12"
          >
            {{ contact.name }}
          </h3>
          <div class="flex flex-row items-center gap-2">
            <span
              v-if="contact.created_at"
              v-tooltip.left="
                `${$t('CONTACT_PANEL.CREATED_AT_LABEL')} ${dynamicTime(
                  contact.created_at
                )}`
              "
              class="i-lucide-info text-sm text-n-slate-10"
            />
            <a
              :href="contactProfileLink"
              target="_blank"
              rel="noopener nofollow noreferrer"
              class="leading-3"
            >
              <span class="i-lucide-external-link text-sm text-n-slate-10" />
            </a>
          </div>
        </div>

        <p v-if="additionalAttributes.description" class="break-words mb-0.5">
          {{ additionalAttributes.description }}
        </p>
        <div class="flex flex-col items-start w-full gap-2">
          <ContactInfoRow
            :href="contact.email ? `mailto:${contact.email}` : ''"
            :value="contact.email"
            icon="mail"
            emoji="✉️"
            :title="$t('CONTACT_PANEL.EMAIL_ADDRESS')"
            show-copy
          />
          <ContactInfoRow
            :href="contact.phone_number ? `tel:${contact.phone_number}` : ''"
            :value="contact.phone_number"
            icon="call"
            emoji="📞"
            :title="$t('CONTACT_PANEL.PHONE_NUMBER')"
            show-copy
          />
          <ContactInfoRow
            v-if="contact.identifier"
            :value="contact.identifier"
            icon="contact-identify"
            emoji="🪪"
            :title="$t('CONTACT_PANEL.IDENTIFIER')"
          />
          <ContactInfoRow
            :value="additionalAttributes.company_name"
            icon="building-bank"
            emoji="🏢"
            :title="$t('CONTACT_PANEL.COMPANY')"
          />
          <ContactInfoRow
            v-if="location || additionalAttributes.location"
            :value="location || additionalAttributes.location"
            icon="map"
            emoji="🌍"
            :title="$t('CONTACT_PANEL.LOCATION')"
          />
          <SocialIcons :social-profiles="socialProfiles" />
        </div>
      </div>
      <div class="flex items-center w-full mt-0.5 gap-2">
        <!-- WAVOIP Softphone -->
        <SoftphoneMinimized />        
        <!-- BOTÃO WAVOIP COM FEATURE FLAG 
        <NextButton
          v-if="isAPIInbox && isWavoipFeatureEnabled && showWavoipButton"
          v-tooltip.top-end="'Ligar via Wavoip'"
          icon="i-ph-phone"
          emerald
          faded
          sm
          @click="openSoftphone"
        /> -->

        <NextButton
          v-if="isAPIInbox && isWavoipFeatureEnabled && showWavoipButton"
          v-tooltip.top-end="'Ligar via Wavoip'"
          emerald
          faded
          sm
          @click="openSoftphone"
        >
          <template #icon>
            <svg 
              class="w-4 h-4" 
              viewBox="0 0 250 250" 
              fill="currentColor"
            >
              <g transform="translate(0,250) scale(0.1,-0.1)">
                <path d="M2314 2301 c-27 -12 -47 -31 -58 -52 -20 -39 -35 -208 -66 -744 -11 -192 -26 -397 -32 -455 -5 -58 -14 -145 -20 -195 -5 -49 -11 -121 -13 -159 -3 -66 -19 -106 -29 -75 -9 26 -48 223 -56 279 -5 30 -24 170 -44 310 -20 140 -40 281 -43 313 -3 32 -10 60 -14 63 -5 3 -9 25 -9 50 0 24 -4 52 -9 62 -5 9 -17 55 -26 102 -17 84 -58 214 -79 254 -64 118 -138 176 -221 176 -49 -1 -82 -15 -128 -57 -132 -117 -179 -283 -279 -973 -40 -281 -42 -300 -29 -313 15 -15 14 -37 -2 -37 -17 0 -29 34 -70 205 -36 148 -67 256 -89 305 -100 229 -221 340 -368 340 -78 0 -139 -33 -175 -94 -42 -72 -30 -182 27 -232 45 -40 98 -64 143 -64 41 0 46 -3 69 -42 26 -45 55 -137 112 -353 19 -71 42 -152 50 -179 40 -120 126 -237 201 -273 45 -21 127 -22 163 -2 52 30 80 52 80 64 0 6 9 20 21 31 26 24 61 92 83 164 9 30 21 63 27 72 5 10 9 33 9 52 0 19 5 38 10 41 6 3 10 28 10 55 0 27 4 51 9 54 4 3 11 29 14 58 12 107 57 436 73 530 9 54 16 112 17 130 1 22 8 34 20 36 13 3 17 -4 17 -30 0 -18 12 -96 26 -173 14 -77 30 -171 34 -210 48 -374 115 -782 131 -799 5 -6 9 -19 9 -31 0 -11 9 -45 21 -75 11 -30 24 -67 28 -81 31 -99 130 -227 198 -256 65 -28 124 -13 187 50 77 75 132 222 170 452 34 200 67 623 67 833 0 79 3 132 9 132 17 0 12 107 -5 114 -8 3 -15 10 -15 16 0 5 7 10 15 10 8 0 15 7 15 15 0 8 -7 15 -15 15 -8 0 -15 7 -15 15 0 9 7 18 15 21 13 5 15 43 15 253 0 247 0 247 -42 274 -49 32 -87 34 -144 8z"/>
              </g>
            </svg>
          </template>
        </NextButton>

        <!-- FreePBX -->
        <NextButton
          v-if="isAPIInbox && isFreePBXFeatureEnabled"
          v-tooltip.top-end="'Ligar via Voip'"
          icon="i-ph-phone"
          emerald
          faded
          sm
          @click="openSoftphone"
        />

        <ComposeConversation
          :contact-id="String(contact.id)"
          is-modal
          @close="closeComposeConversationModal"
        >
          <template #trigger="{ toggle }">
            <NextButton
              v-tooltip.top-end="$t('CONTACT_PANEL.NEW_MESSAGE')"
              icon="i-ph-chat-circle-dots"
              slate
              faded
              sm
              @click="openComposeConversationModal(toggle)"
            />
          </template>
        </ComposeConversation>
        <NextButton
          v-tooltip.top-end="$t('EDIT_CONTACT.BUTTON_LABEL')"
          icon="i-ph-pencil-simple"
          slate
          faded
          sm
          @click="toggleEditModal"
        />
        <NextButton
          v-tooltip.top-end="$t('CONTACT_PANEL.MERGE_CONTACT')"
          icon="i-ph-arrows-merge"
          slate
          faded
          sm
          :disabled="uiFlags.isMerging"
          @click="openMergeModal"
        />
        <NextButton
          v-if="isAdmin"
          v-tooltip.top-end="$t('DELETE_CONTACT.BUTTON_LABEL')"
          icon="i-ph-trash"
          slate
          faded
          sm
          ruby
          :disabled="uiFlags.isDeleting"
          @click="toggleDeleteModal"
        />
      </div>
      <EditContact
        v-if="showEditModal"
        :show="showEditModal"
        :contact="contact"
        @cancel="toggleEditModal"
      />
      <ContactMergeModal
        v-if="showMergeModal"
        :primary-contact="contact"
        :show="showMergeModal"
        @close="closeMergeModal"
      />
    </div>
    <woot-delete-modal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />
  </div>
</template>
